import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Plus, Search } from 'lucide-react';
import { useInsumos } from '../api';
import type { Insumo } from '../schemas';
import { capitaliza, fmtDose, fmtNum } from '../insumos-fmt';
import { NovoInsumoModal } from './NovoInsumoModal';

// Tela 01 · Catálogo de Insumos (desktop) — produtos usados nas recomendações
// e aplicações. Layout fiel ao Figma (🧪 ARA · Insumos & Aplicações):
// header + busca + filtro segmentado por categoria + tabela enxuta (produto,
// marca, composição, grupo químico, dose, status). Formulação, carência e
// máx. aplicações ficam só na ficha do produto.
// Abas em ordem alfabética (Todos primeiro).
const CATEGORIAS = [
  { key: '', rotulo: 'Todos' },
  { key: 'biologico', rotulo: 'Biológicos' },
  { key: 'especialidade', rotulo: 'Especialidades' },
  { key: 'adubacao', rotulo: 'Fertilizantes' },
  { key: 'fungicida', rotulo: 'Fungicidas' },
  { key: 'herbicida', rotulo: 'Herbicidas' },
  { key: 'inseticida', rotulo: 'Inseticidas' },
  { key: 'nutricao', rotulo: 'Nutrição/Fisiologia' },
  { key: 'semente', rotulo: 'Sementes' },
] as const;

// Colunas: Produto, Marca, Composição, Dose, Status. Grupo químico só na ficha.
const GRID =
  'grid grid-cols-[minmax(150px,1.2fr)_100px_minmax(280px,2.4fr)_180px_90px] items-center gap-3 px-4';

export function CatalogoInsumosView() {
  const navigate = useNavigate();
  const { data: insumos, isLoading, isError } = useInsumos();
  const [busca, setBusca] = useState('');
  const [categoria, setCategoria] = useState('');
  const [novoAberto, setNovoAberto] = useState(false);
  const abrirFicha = (id: number) =>
    void navigate({ to: '/agronomia/insumos/$id', params: { id: String(id) } });

  const lista = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return (insumos ?? []).filter((i) => {
      if (categoria && i.categoria !== categoria) return false;
      if (!q) return true;
      return (
        i.nome_comercial.toLowerCase().includes(q) ||
        (i.fabricante ?? '').toLowerCase().includes(q) ||
        i.ativos.some((a) => a.nome.toLowerCase().includes(q))
      );
    });
  }, [insumos, busca, categoria]);

  return (
    <div className="flex h-full flex-col gap-5 overflow-y-auto bg-bg px-8 pb-8 pt-7">
      {/* header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex flex-col gap-1.5">
          <span className="text-[10.5px] font-semibold uppercase tracking-[0.8px] text-accent">
            Agronomia
          </span>
          <h1 className="text-2xl font-semibold leading-tight text-text">Insumos</h1>
        </div>
        <button
          type="button"
          onClick={() => setNovoAberto(true)}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-[var(--accent)] px-[18px] py-[11px] text-[13px] font-medium text-white"
        >
          <Plus size={14} strokeWidth={2.25} /> Novo insumo
        </button>
      </div>
      {novoAberto && <NovoInsumoModal onClose={() => setNovoAberto(false)} />}

      {/* busca (linha própria) + categorias que quebram, sempre todas visíveis */}
      <div className="flex flex-col gap-3">
        <div className="relative w-full max-w-md">
          <Search
            size={14}
            strokeWidth={1.75}
            className="absolute left-3.5 top-1/2 -translate-y-1/2 text-text-muted"
          />
          <input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar produto, ativo ou fabricante…"
            className="w-full rounded-full border border-black/10 bg-surface py-[9px] pl-9 pr-4 text-[13px] text-text placeholder:text-text-muted focus:outline-none dark:border-white/10"
          />
        </div>
        <div className="flex flex-wrap gap-1 self-start rounded-[10px] bg-surface2 p-[3px]">
          {CATEGORIAS.map((c) => (
            <button
              key={c.key}
              type="button"
              onClick={() => setCategoria(c.key)}
              className={`whitespace-nowrap rounded-[7px] px-3 py-1.5 text-[12.5px] ${
                categoria === c.key
                  ? 'bg-surface font-medium text-text shadow-[0_1px_2px_rgba(0,0,0,0.07)]'
                  : 'text-text-2'
              }`}
            >
              {c.rotulo}
            </button>
          ))}
        </div>
      </div>

      {/* tabela — rola na horizontal quando a área encolhe (não espreme colunas);
          scroll-x-bar mantém a barra sempre visível e pegável (macOS/WebView) */}
      <div className="scroll-x-bar overflow-x-auto rounded-[14px] border border-[var(--agro-card-border)] bg-surface">
        <div className="min-w-[1080px]">
          <div
            className={`${GRID} sticky top-0 z-10 border-b border-black/[0.05] bg-[#f8f8f7] py-2.5 dark:border-white/[0.06] dark:bg-[var(--surface2)]`}
          >
            {['Produto', 'Marca', 'Composição', 'Dose (bula)', 'Status'].map((h) => (
              <span key={h} className="text-[10.5px] font-semibold uppercase tracking-[0.5px] text-text-muted">
                {h}
              </span>
            ))}
          </div>

          {isLoading && <p className="px-4 py-8 text-center text-sm text-text-muted">Carregando catálogo…</p>}
          {isError && <p className="px-4 py-8 text-center text-sm text-[var(--danger)]">Falha ao carregar o catálogo.</p>}
          {!isLoading && !isError && lista.length === 0 && (
            <p className="px-4 py-8 text-center text-sm text-text-muted">
              {busca || categoria ? 'Nenhum produto encontrado com esse filtro.' : 'Nenhum insumo cadastrado ainda.'}
            </p>
          )}

          {lista.map((i, idx) => (
            <LinhaInsumo key={i.id} insumo={i} zebra={idx % 2 === 1} onClick={() => abrirFicha(i.id)} />
          ))}

          <div className="border-t border-black/[0.08] px-4 py-2.5 dark:border-white/[0.08]">
            <span className="text-[11.5px] text-text-muted">
              Fonte: bulas ADAPAR — doses por cultura conforme bula vigente; carência e limites no
              detalhe do produto; receituário agronômico obrigatório.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function LinhaInsumo({ insumo: i, zebra, onClick }: { insumo: Insumo; zebra: boolean; onClick: () => void }) {
  const soja = i.culturas_bula.find((c) => c.cultura === 'soja');
  const outras = i.culturas_bula.filter((c) => c.cultura !== 'soja');

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
      className={`${GRID} cursor-pointer py-3 hover:bg-[var(--accent-100)]/30 ${zebra ? 'bg-[#fcfcfb] dark:bg-white/[0.015]' : ''}`}
    >
      <span className="text-[13px] font-medium text-text">{i.nome_comercial}</span>
      <span className="text-[11.5px] text-text-muted">{i.fabricante ?? '—'}</span>
      <span className="flex flex-col gap-0.5">
        {i.ativos.map((a, idx) => (
          <span key={idx} className="text-xs leading-snug text-text-2">
            {a.nome} {fmtNum(a.concentracao)}
            {a.concentracao_unidade ? ` ${a.concentracao_unidade}` : ''}
          </span>
        ))}
        {i.ativos.length === 0 && <span className="text-xs text-text-muted">—</span>}
      </span>
      <span className="flex flex-col gap-0.5">
        {soja && <span className="text-xs text-text-2">Soja {fmtDose(soja, true)}</span>}
        {outras.map((c) => (
          <span key={c.cultura} className="text-[11px] text-text-muted">
            {capitaliza(c.cultura)} {fmtDose(c, false)}
          </span>
        ))}
        {i.culturas_bula.length === 0 && <span className="text-xs text-text-muted">—</span>}
      </span>
      <span>
        {i.ativo ? (
          <span className="inline-flex items-center gap-[5px] rounded-full bg-[rgba(50,150,95,0.10)] px-2 py-[3px] text-[11px] font-medium text-[#32965f]">
            <span className="size-1.5 rounded-full bg-[#32965f]" /> Ativo
          </span>
        ) : (
          <span className="inline-flex items-center gap-[5px] rounded-full bg-surface3 px-2 py-[3px] text-[11px] font-medium text-text-2">
            <span className="size-1.5 rounded-full bg-text-muted" /> Inativo
          </span>
        )}
      </span>
    </div>
  );
}
